/**
 * The only script on public pages. It enhances server-rendered HTML that already works without it:
 * disclosure menus, the shared theme and language preference, the `/` redirect, print buttons, the demo
 * request hand-off and the contact form.
 */
import {
    applyTheme,
    configurePreferences,
    rememberLocaleChoice,
    resolveTheme,
    storedLocale,
    storedTheme,
    transitionTheme,
} from '@hlt/web-kit/preferences';
import { enhanceContactForm, enhanceDemoForms } from '@/contact-form';
import { bestLocale, isPublicLocale } from '@/locales';

// The theme and language choice is shared with the apps through a cookie on their common parent domain.
configurePreferences((document.querySelector<HTMLMetaElement>('meta[name="hlt-hosts"]')?.content ?? '').split(' '));

/**
 * The header's light/dark switch (and its phone-menu row): hidden without JavaScript, then kept in step
 * with the shared preference and the system setting.
 */
function enhanceThemeControls(): void {
    const toggles = [...document.querySelectorAll<HTMLButtonElement>('button[data-theme-toggle]')];
    const sync = () => {
        const current = resolveTheme();
        for (const button of toggles) {
            const icons = button.querySelector<HTMLElement>('.hlt-theme-toggle');
            if (icons) icons.dataset.scheme = current;
            const label = current === 'dark' ? button.dataset.labelLight : button.dataset.labelDark;
            if (!label) continue;
            const text = button.querySelector<HTMLElement>('[data-theme-toggle-text]');
            if (text) text.textContent = label;
            else {
                button.setAttribute('aria-label', label);
                button.title = label;
            }
        }
    };
    sync();
    for (const button of toggles) {
        button.hidden = false;
        button.addEventListener('click', () => {
            const rect = button.getBoundingClientRect();
            transitionTheme(resolveTheme() === 'dark' ? 'light' : 'dark', {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            });
            sync();
        });
    }
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', sync);
    // With View Transitions the new theme is applied a frame later, so labels follow the document itself.
    new MutationObserver(sync).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme', 'data-mantine-color-scheme'],
    });
    // Another HLT app or tab may have changed the shared cookie while this page was in the background.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        const preference = storedTheme();
        if (preference !== (document.documentElement.dataset.theme ?? 'system'))
            applyTheme(preference, { persist: false });
        sync();
    });
}

/** Native picture selection follows the system without JS and the saved site preference with JS. */
function enhanceScreenshotTheme(): void {
    const sources = document.querySelectorAll<HTMLSourceElement>('[data-product-screenshot] source');
    if (!sources.length) return;
    const sync = () => {
        const theme = document.documentElement.dataset.theme;
        for (const source of sources)
            source.media = theme === 'dark' ? 'all' : theme === 'light' ? 'not all' : '(prefers-color-scheme: dark)';
    };
    sync();
    new MutationObserver(sync).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
}

function enhanceDisclosures(): void {
    const menus = [...document.querySelectorAll<HTMLDetailsElement>('details[data-disclosure]')];
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        for (const menu of menus) if (menu.open && !menu.contains(target)) menu.open = false;
    });
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        for (const menu of menus) {
            if (!menu.open) continue;
            menu.open = false;
            menu.querySelector('summary')?.focus();
        }
    });
    for (const link of document.querySelectorAll<HTMLAnchorElement>('a[data-locale]')) {
        link.addEventListener('click', () => {
            const locale = link.dataset.locale ?? '';
            if (isPublicLocale(locale)) rememberLocaleChoice(locale);
        });
    }
}

function redirectFromRoot(): void {
    if (!document.querySelector('[data-root-redirect]')) return;
    // An explicit choice, otherwise the browser's language on this visit (detection is never stored).
    const locale = bestLocale(storedLocale(), navigator.languages);
    window.location.replace(`/${locale}/${window.location.hash}`);
}

function enhancePrint(): void {
    for (const button of document.querySelectorAll<HTMLButtonElement>('button[data-print]')) {
        button.hidden = false;
        button.addEventListener('click', () => window.print());
    }
}

enhanceThemeControls();
enhanceScreenshotTheme();
redirectFromRoot();
enhanceDisclosures();
enhancePrint();
enhanceDemoForms();
enhanceContactForm();
